/* eslint-disable no-unused-vars */
import React, { useCallback, useState } from 'react';

import PropTypes from 'prop-types';
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from 'antd';
import { useQuery } from '../../hooks/useQuery';
import { numberWithSpacesInter } from '../../helpers/formatInteger';
import dayjs from 'dayjs';
import ModalDeletePlanStage from './ModalDeletePlanStage';

import { ReactComponent as EditIcon } from '../../assets/edit-icon.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete-white-icon.svg';
import { ReactComponent as BlueDotIcon } from './img/blue-dot-icon.svg';
import { ReactComponent as OrangeDotIcon } from './img/orange-dot-icon.svg';

const FinplanStegeItem = ({
  accountId,
  stage,
  count = 0,
  setOpenCreateStageModal,
  categoryObj,
  isLast,
  withoutSelector,
  currentLang,
}) => {
  const { url } = useRouteMatch();
  const history = useHistory();
  const { search } = useLocation();
  const query = useQuery(search);
  const { t } = useTranslation();

  const globalCategoryParam = query.get('global-category');
  const globalCategories = globalCategoryParam
    ? globalCategoryParam.split('+')
    : [];
  const itemTypeSelected = query.get('item-type');
  const viewType = query.get('view-type');
  const [modalDelete, setModalDelete] = useState(null);

  const width = count ? 100 / count : 0;
  const style = {
    width: `${width}%`,
  };

  const getTotal = useCallback(
    (type) => {
      const items = stage?.[type] ?? [];

      return items.reduce((sum, item) => sum + item?.avg_amount_yearly_sek, 0);
    },
    [stage],
  );

  const getCategoryHeight = useCallback(
    (category) => {
      if (stage?.[itemTypeSelected]?.length) {
        const items = stage?.[itemTypeSelected] ?? [];

        const total = items.reduce(
          (acc, item) => acc + item?.avg_amount_yearly_sek,
          0,
        );

        const el = items.filter((i) => i.global_category === category);
        if (el?.length) {
          const elTotal = Math.round(
            el.reduce((acc, item) => acc + item?.avg_amount_yearly_sek, 0),
          );
          const result = total > 0 ? Math.round((elTotal * 100) / total) : 0;
          return { result, total: numberWithSpacesInter(elTotal, 0) };
        } else {
          return { result: 0, total: numberWithSpacesInter(0, 0) };
        }
      }
      return { result: 33, total: numberWithSpacesInter(0, 0) };
    },
    [stage, itemTypeSelected],
  );

  const handleClick = (id) => {
    const isSelected = globalCategories.includes(id);
    let newCategories;

    if (isSelected) {
      newCategories = globalCategories.filter((cat) => cat !== id);
    } else {
      newCategories = [...globalCategories, id];
    }

    const categoriesParam =
      newCategories.length > 0 ? newCategories.join('+') : '';
    const searchParams = new URLSearchParams();
    searchParams.set('item-type', itemTypeSelected);
    if (categoriesParam) {
      searchParams.set('global-category', categoriesParam);
    }
    searchParams.set('view-type', viewType || 'table');

    if (count) {
      history.push({
        pathname: `${url}/${stage?.id}`,
        search: searchParams.toString(),
      });
    } else {
      history.push({
        pathname: url,
        search: searchParams.toString(),
      });
    }
  };

  const handleDeleteModal = () => {
    setModalDelete(stage?.id);
  };

  const handleSelectItemType = (newItemType) => {
    const searchParams = new URLSearchParams(search);
    searchParams.set('item-type', newItemType);

    // const availableCategories =
    //   categoryObj?.[newItemType]?.map((cat) => cat.id) || [];

    // if (availableCategories.length > 0) {
    //   searchParams.set('global-category', availableCategories.join('+'));
    // } else {
    //   searchParams.delete('global-category');
    // }

    history.replace({
      pathname: window.location.pathname,
      search: searchParams.toString(),
    });
  };

  const handleSelectAll = () => {
    const searchParams = new URLSearchParams(search);

    const availableCategories =
      categoryObj?.[itemTypeSelected]?.map((cat) => cat.id) || [];

    if (availableCategories.length > 0) {
      searchParams.set('global-category', availableCategories.join('+'));
    } else {
      searchParams.delete('global-category');
    }

    history.push({
      pathname: `${url}/${stage?.id}`,
      search: searchParams.toString(),
    });
  };

  return (
    <div className="finplan-stageitem-wrapper" style={style}>
      <div className="finplan-stageitem-label-row">
        <div
          className="finplan-stageitem-label"
          onClick={!withoutSelector ? () => handleSelectAll() : null}
        >
          {stage?.name} <span>{`${stage?.start_year}-${stage?.end_year}`}</span>
        </div>
        <Button
          type="text"
          icon={<EditIcon />}
          className="finplan-stageitem-btn-edit"
          onClick={() => setOpenCreateStageModal(stage?.id)}
        />
        {isLast && (
          <Button
            type="text"
            icon={<DeleteIcon />}
            className="finplan-stageitem-btn-edit"
            onClick={() => handleDeleteModal()}
          />
        )}
      </div>
      {!withoutSelector && (
        <div className="finplan-total-income">
          <p
            onClick={() => handleSelectItemType('incomes')}
            className={`finplan-total-income-label ${itemTypeSelected === 'incomes' ? 'selected' : ''}`}
          >
            <BlueDotIcon />
            <span className="label">{t('AVERAGE_ANNUAL_INCOME')}</span>
            <span className="amount">
              {numberWithSpacesInter(getTotal('incomes'), 0)}
            </span>
            <span className="currency">kr</span>
          </p>
        </div>
      )}
      {categoryObj &&
        categoryObj?.[itemTypeSelected]?.map(({ id, name }) => {
          return (
            <div
              key={id}
              className={`finplan-stageitem-expens ${globalCategories.includes(id) ? 'selected' : ''} ${itemTypeSelected}`}
              style={{ height: `${getCategoryHeight(id).result}%` }}
              onClick={() => handleClick(id)}
            >
              <div className="finplan-stageitem-header">
                <p className="finplan-stageitem-data">
                  {itemTypeSelected === 'incomes' ? (
                    <>
                      <BlueDotIcon />
                      <span className="label">{t('INCOME')}</span>
                    </>
                  ) : (
                    <>
                      <OrangeDotIcon />
                      <span className="label">{t('SPEND')}</span>
                    </>
                  )}
                </p>
                <div className="finplan-stageitem-data-wrapper">
                  <p className="finplan-stageitem-data">
                    {getCategoryHeight(id).result}%
                  </p>

                  <p className="finplan-stageitem-value">
                    {getCategoryHeight(id).total} kr
                  </p>
                </div>
              </div>
              <div className="finplan-stages-label-item">
                <div className={`fp-dot-${id}`} />
                <span className="">{t(name[currentLang])}</span>
              </div>
            </div>
          );
        })}
      {!withoutSelector && (
        <div className="finplan-total-income">
          <p
            onClick={() => handleSelectItemType('expenses')}
            className={`finplan-total-income-label ${itemTypeSelected === 'expenses' ? 'selected' : ''}`}
          >
            <OrangeDotIcon />
            <span className="label">{t('AVERAGE_ANNUAL_SPEND')}</span>
            <span className="amount">
              {numberWithSpacesInter(getTotal('expenses'), 0)}
            </span>
            <span className="currency">kr</span>
          </p>
        </div>
      )}
      <ModalDeletePlanStage
        accountId={accountId}
        modalDelete={modalDelete}
        setModalDelete={setModalDelete}
      />
    </div>
  );
};

export default FinplanStegeItem;

FinplanStegeItem.propTypes = {
  accountId: PropTypes.string,
  stage: PropTypes.object,
  count: PropTypes.number,
  setOpenCreateStageModal: PropTypes.func,
  currentLang: PropTypes.string,
  itemType: PropTypes.string,
  categoryObj: PropTypes.object,
  isLast: PropTypes.bool,
  withoutSelector: PropTypes.bool,
};
